import { useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import {
  setData,
  setIsMadaVisible,
  setIsVisible,
} from "./apis/slices/hyperPaySlice";
import { AppDispatch, RootState } from "./apis/store";
import HyperPayForm from "./Components/PaymentsAndPoints/HyperPayForm";
import PaymentMethod from "./Components/PaymentsAndPoints/PaymentMethod";
import AppRoutes from "./routes/AppRoutes";
import "./styles/App.scss";
import "./translations/i18";
import "@emran-alhaddad/saudi-riyal-font/index.css";
import MadaForm from "./Components/PaymentsAndPoints/HyperPayFormMada";
import { clearLanguageSwitchSplash } from "./utils/languageSwitchSplash";
import { baseUrl } from "./proxy"
import ThemeLoader from "./theme/ThemeLoader";
import { requestCartSync } from "./apis/actions/cart.actions";
import { useCookies } from "react-cookie";

function joinUrl(base: string, path: string) {
  try {
    return new URL(path, base).toString();
  } catch {
    return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
  }
}

const loadScript = (src: string, id: string) =>
  new Promise<void>((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }

    const script = document.createElement("script");

    script.src = src;
    script.id = id;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));

    document.body.appendChild(script);
  });

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const [cookies] = useCookies(["apiKey"]);

  useEffect(() => {
    if (!cookies.apiKey) return;
    const refresh = () => {
      if (document.visibilityState === "visible") dispatch(requestCartSync());
    };
    dispatch(requestCartSync());
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [cookies.apiKey, dispatch]);

  const { isVisible, isMadaVisible } = useSelector(
    (state: RootState) => state?.hyperPay
  );
  const { my_cart, showPaiement } = useSelector(
    (state: RootState) => state?.global
  );

  useEffect(() => {
    /* The loader sets odoo.__session_info__.livechatData; the embed crashes (owl
       lifecycle) if it runs first or if livechat is not available on the DB. */
    loadScript(joinUrl(baseUrl, "/im_livechat/loader/1"), "im_livechat")
      .then(() => {
        const odoo = (window as any).odoo;
        if (!odoo?.__session_info__?.livechatData?.isAvailable) return;
        return loadScript(
          joinUrl(baseUrl, "/im_livechat/assets_embed.js"),
          "im_livechat-asset"
        );
      })
      .catch(() => {
        /* livechat is optional — never break the app over it */
      });

    localStorage.setItem("theme", "light");
  }, []);

  /* Hide `#ac-lang-switch-overlay` (public/index.html) as soon as React mounts — no fixed 3s wait */
  useLayoutEffect(() => {
    clearLanguageSwitchSplash();
  }, []);

  return (
    <>
      <ThemeLoader />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      {isVisible && (
        <HyperPayForm
          isOpen={isVisible}
          setIsOpen={() => {
            dispatch(setIsVisible(false));
            dispatch(setData(null));
          }}
        />
      )}
      {isMadaVisible && (
        <MadaForm
          isOpen={isMadaVisible}
          setIsOpen={() => {
            dispatch(setIsMadaVisible(false));
            dispatch(setData(null));
          }}
        />
      )}
      <PaymentMethod visible={showPaiement} />
    </>
  );
}

export default App;
